import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, Download, FileText, RefreshCw, Phone, Mail, Undo2, AlertTriangle, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ledgersAPI } from '@/services/api';
import { VoidPaymentDialog } from '@/components/hq/VoidPaymentDialog';

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

// Visual label for each ledger line: what it is and which side it hits
const lineKind = (e) => {
  const et = e.entry_type, rt = e.reference_type || '';
  if (et === 'payment' || rt === 'payment') return { label: 'Payment', cls: 'bg-emerald-100 text-emerald-700' };
  if (et === 'payment_reversal' || rt === 'payment_reversal') return { label: 'Payment Void', cls: 'bg-red-100 text-red-700' };
  if (rt === 'purchase_return' || et === 'credit_note') return { label: 'Return / Credit Note', cls: 'bg-blue-100 text-blue-700' };
  if (et === 'adjustment' || rt.includes('correction')) return { label: 'Correction', cls: 'bg-gray-200 text-gray-700' };
  if (et === 'debit_note') return { label: 'Debit Note', cls: 'bg-amber-100 text-amber-700' };
  if (et === 'opening_balance') return { label: 'Opening', cls: 'bg-gray-100 text-gray-600' };
  return { label: 'Purchase (GRN)', cls: 'bg-amber-100 text-amber-700' };
};

const Stat = ({ label, value, sub, tone = 'default', testId }) => {
  const tones = {
    default: 'text-gray-800',
    purchase: 'text-amber-700',
    paid: 'text-emerald-700',
    returns: 'text-blue-700',
    due: 'text-red-700',
  };
  return (
    <div className="px-5 py-4 border-r last:border-r-0 border-gray-100 min-w-[150px] flex-1">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-xl font-bold mt-1 ${tones[tone]}`} data-testid={testId}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
};

const VendorStatementPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [ledger, setLedger] = useState(null);
  const [entries, setEntries] = useState([]);
  const [overdue, setOverdue] = useState(null);
  const [voidEntry, setVoidEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const ledgerRes = await ledgersAPI.getVendorLedger(vendorId);
      setLedger(ledgerRes.data);
      const params = { page: 1, page_size: 200, order: 'asc' };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const [entriesRes, dueRes] = await Promise.all([
        ledgersAPI.getVendorLedgerEntries(vendorId, params),
        ledgersAPI.vendorPaymentReminders({ vendor_id: vendorId, days_ahead: 7 }).catch(() => null),
      ]);
      setEntries(entriesRes.data?.items || []);
      setOverdue(dueRes?.data?.summary || null);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch statement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [vendorId, fromDate, toDate, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportParams = () => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    return params;
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ledgers/vendor/${vendorId}/export/csv?${exportParams()}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendor_statement_${ledger?.vendor_name?.replace(/\s+/g, '_') || vendorId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export statement', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = () => {
    const params = exportParams();
    params.append('token', localStorage.getItem('access_token'));
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/ledgers/vendor/${vendorId}/export/pdf?${params}`, '_blank');
  };

  if (loading && !ledger) {
    return <div className="p-8 text-center text-gray-500">Loading statement...</div>;
  }

  const balance = Number(ledger?.current_balance || 0);
  const visible = entries.filter(e => e.entry_type !== 'opening_balance');
  const periodDebit = visible.reduce((s, e) => s + Number(e.debit_amount || 0), 0);
  const periodCredit = visible.reduce((s, e) => s + Number(e.credit_amount || 0), 0);
  const terms = ledger?.payment_terms_days ? `${ledger.payment_terms_days} days credit` : (ledger?.payment_terms || '').replace(/_/g, ' ');

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="vendor-statement-page">
      <Toaster />
      <VoidPaymentDialog open={!!voidEntry} onOpenChange={(o) => !o && setVoidEntry(null)} payment={voidEntry} onDone={fetchData} />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/hq/ledgers/vendor')} className="h-10 w-10 shrink-0" data-testid="statement-back-btn">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Button>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Vendor Statement</p>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="statement-vendor-name">{ledger?.vendor_name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
            <span className="font-mono text-purple-700">{ledger?.vendor_code}</span>
            {ledger?.vendor_phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {ledger.vendor_phone}</span>}
            {ledger?.vendor_email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {ledger.vendor_email}</span>}
            {ledger?.gstin && <span>GSTIN {ledger.gstin}</span>}
            {terms && <span className="capitalize">{terms}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportCSV} disabled={exporting} className="border-gray-300" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="border-purple-300 text-purple-600 hover:bg-purple-50" data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" /> Print PDF
          </Button>
          <Button variant="outline" onClick={fetchData} className="border-gray-300" data-testid="statement-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* One summary strip */}
      <Card className="bg-white border-gray-200 mb-6 overflow-hidden">
        <div className="flex flex-wrap divide-y lg:divide-y-0">
          <Stat label="Opening" value={formatCurrency(ledger?.opening_balance)} />
          <Stat label="Purchases (GRN)" value={formatCurrency(ledger?.total_purchases)} sub="adds to payable" tone="purchase" testId="statement-purchases" />
          <Stat label="Paid" value={formatCurrency(ledger?.total_paid)} sub={ledger?.last_payment_date ? `last ${formatDate(ledger.last_payment_date)}` : 'no payments yet'} tone="paid" testId="statement-paid" />
          <Stat label="Returns & Credits" value={formatCurrency(ledger?.total_returns)} sub="returns, credit notes, corrections" tone="returns" testId="statement-returns" />
          <div className={`px-5 py-4 min-w-[200px] flex-1 ${balance > 0 ? 'bg-red-50' : balance < 0 ? 'bg-blue-50' : 'bg-emerald-50'}`}>
            <p className="text-[11px] uppercase tracking-wide text-gray-600">{balance > 0 ? 'Balance payable' : balance < 0 ? 'Advance with vendor' : 'Balance'}</p>
            <p className={`text-2xl font-bold mt-1 ${balance > 0 ? 'text-red-700' : balance < 0 ? 'text-blue-700' : 'text-emerald-700'}`} data-testid="statement-balance">{formatCurrency(Math.abs(balance))}</p>
            {overdue && overdue.overdue_amount > 0 ? (
              <button onClick={() => navigate('/hq/ledgers/vendor')} className="text-xs text-red-700 font-semibold flex items-center gap-1 mt-0.5 hover:underline" data-testid="statement-overdue">
                <AlertTriangle className="w-3 h-3" /> {formatCurrency(overdue.overdue_amount)} overdue · {overdue.overdue_count} bill(s)
              </button>
            ) : (
              <p className="text-xs text-gray-500 mt-0.5">Opening + Purchases − Paid − Returns</p>
            )}
          </div>
        </div>
      </Card>

      {/* The statement: one table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="font-semibold text-gray-800">Account statement</p>
            <div className="flex items-center gap-2 sm:ml-auto text-sm">
              <span className="text-gray-500">From</span>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-40 bg-white border-gray-300" data-testid="statement-from-date" />
              <span className="text-gray-500">To</span>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-40 bg-white border-gray-300" data-testid="statement-to-date" />
              {(fromDate || toDate) && (
                <Button variant="ghost" size="sm" onClick={() => { setFromDate(''); setToDate(''); }} className="text-gray-500" data-testid="statement-clear-dates">Clear</Button>
              )}
            </div>
          </div>

          {visible.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-30" />
              No transactions {fromDate || toDate ? 'in this period' : 'yet'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-700 w-[110px]">Date</TableHead>
                  <TableHead className="font-semibold text-gray-700">Particulars</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Purchases / Charges</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Payments / Credits</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Balance</TableHead>
                  <TableHead className="w-[40px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {!fromDate && !toDate && (
                  <TableRow className="bg-gray-50/60">
                    <TableCell className="text-gray-500 text-sm">—</TableCell>
                    <TableCell className="text-gray-600 text-sm font-medium">Opening balance</TableCell>
                    <TableCell /><TableCell />
                    <TableCell className="text-right font-medium text-gray-700">{formatCurrency(ledger?.opening_balance)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
                {visible.map((entry) => {
                  const kind = lineKind(entry);
                  const isPayment = kind.label === 'Payment';
                  return (
                    <TableRow key={entry.id} className={`hover:bg-gray-50 ${entry.reversed ? 'opacity-60' : ''}`} data-testid={`statement-row-${entry.id}`}>
                      <TableCell className="text-gray-700 text-sm whitespace-nowrap">{formatDate(entry.entry_date)}</TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <Badge className={`${kind.cls} border-0 text-[11px] font-semibold shrink-0 mt-0.5`}>{kind.label}</Badge>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800">
                              {entry.reference_number || '—'}
                              {entry.reversed && <span className="ml-2 text-[11px] text-red-600 font-semibold">VOIDED</span>}
                            </p>
                            {entry.description && <p className="text-xs text-gray-500 truncate max-w-[520px]" title={entry.description}>{entry.description}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-amber-700 font-medium">{Number(entry.credit_amount) > 0 ? formatCurrency(entry.credit_amount) : ''}</TableCell>
                      <TableCell className={`text-right font-medium ${isPayment ? 'text-emerald-700' : 'text-blue-700'}`}>{Number(entry.debit_amount) > 0 ? formatCurrency(entry.debit_amount) : ''}</TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">{formatCurrency(entry.running_balance)}</TableCell>
                      <TableCell>
                        {isPayment && !entry.reversed && entry.reference_id && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Void this payment"
                            onClick={() => setVoidEntry({ id: entry.reference_id, payment_number: entry.reference_number, amount: entry.debit_amount })}
                            data-testid={`void-payment-${entry.reference_number}`}>
                            <Undo2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="bg-gray-50 border-t-2 border-gray-200">
                  <TableCell />
                  <TableCell className="font-semibold text-gray-700">{fromDate || toDate ? 'Period totals' : 'Totals'} · {visible.length} transaction(s)</TableCell>
                  <TableCell className="text-right font-bold text-amber-700">{formatCurrency(periodCredit)}</TableCell>
                  <TableCell className="text-right font-bold text-emerald-700">{formatCurrency(periodDebit)}</TableCell>
                  <TableCell className={`text-right font-bold ${balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{formatCurrency(Math.abs(balance))}{balance > 0 ? ' Dr' : balance < 0 ? ' Cr' : ''}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorStatementPage;
